// src/pages/InsightGenerator.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, LinearProgress, Box, Slider
} from "@mui/material";
import { ContentBuilderPage } from "../components/ContentBuilderPage";
import { useAxiosAuth } from "../hooks/useAxiosAuth";

/* --- Modal just handles form state --- */
function InsightGeneratorModal({ open, onCancel, onGenerate, initialText }) {
  // NEW: keep the text in state so it can be edited
  const [textToVisualize, setTextToVisualize] = useState(initialText?.length > 0 ? initialText : "Describe concept to be explored");
  const [vizType, setVizType] = useState("explanation");
  const [vizSubtype, setVizSubtype] = useState("");
  const [vizName, setVizName] = useState("insight");
  const [loading, setLoading] = useState(false);

  const MIN_WORDS = 400;
  const MAX_WORDS = 8000;
  const [wordCount, setWordCount] = useState(1000);
  const handleWordCountChange = (event, newValue) => {
    setWordCount(newValue);
  };

  // If initialText ever changes from the parent, sync it
  useEffect(() => {
    setTextToVisualize(initialText?.length > 0 ? initialText : "Describe concept to be explored");
  }, [initialText]);

  const handleSubmit = async () => {
    setLoading(true);
    // now pass the edited text back up, too
    try {
      await onGenerate(textToVisualize, { vizName, vizType, vizSubtype, wordCount });
    } catch (e) {
      console.error(e)
      //setError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Concept Explainer</DialogTitle>
      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* scrollable text area */}
        <Box sx={{ flex: '1 1 auto', maxHeight: '40vh', overflowY: 'auto' }}>
          <TextField
            fullWidth
            multiline
            // no longer disabled
            value={textToVisualize}
            onChange={e => setTextToVisualize(e.target.value)}
            margin="normal"
          />
        </Box>

        {/* sticky controls */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            pt: 2,
            pb: 1,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <TextField
            label="Insight Name"
            fullWidth
            value={vizName}
            onChange={e => setVizName(e.target.value)}
            margin="normal"
          />
          <TextField
            select
            label="Insight Type"
            fullWidth
            value={vizType}
            onChange={e => setVizType(e.target.value)}
            margin="normal"
          >
            <MenuItem value="explanation">Explanation</MenuItem>
            <MenuItem value="derivation">Derivation</MenuItem>
            <MenuItem value="summary">Summary</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
            <MenuItem value="placeholder">Placeholder</MenuItem>
          </TextField>
          <Slider
                value={wordCount}
                onChange={handleWordCountChange}
                aria-labelledby="word-count-slider"
                valueLabelDisplay="auto"
                step={500}
                marks={[
                  { value: MIN_WORDS, label: "400" },
                  { value: MAX_WORDS, label: "8000" },
                ]}
                min={MIN_WORDS}
                max={MAX_WORDS}
              />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}


/* --- Page component --- */
export default function InsightGenerator() {
  const axios = useAxiosAuth();

  const generateVisualization = useCallback(
    async (text, courseId, lessonId, { vizName, vizType, vizSubtype, wordCount  }) => {
      const body = {
        selected_text: text,
        course_id: courseId,
        lesson_id: lessonId,
        deepdive_type: vizType,
        word_count: wordCount,
        explanation_title: vizName
      };
      const { data } = await axios.post("/generate_deepdive/", body);
      return data;
    },
    [axios]
  );

  const getAnchorLabel = useCallback((viz) => {
    const { vizType, vizName } = viz?.extraArgs ?? {};
    switch (viz.extraArgs?.vizType) {
      case "summary":
        return "Open summary in new tab";
      case "derivation":
        return "Open derivation in new tab";
      case "custom":
        return vizName && vizName.trim() ? vizName : "Open topic in new tab";        
      case "explanation":
      default:
        return "Open explanation in new tab";
    }
  }, []);

  return (
    <ContentBuilderPage
      instructionText="Highlight text to create an interactive visual."
      modal={<InsightGeneratorModal />}
      generatorFn={generateVisualization}
      getAnchorLabel={getAnchorLabel}
      assetType="deepdives"
    />
  );
}
